import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import type { Metrics } from "@/types/dashboard";
import { chart, scoreColor } from "@/lib/brand";
import { ChartEmpty, ChartHeader, axisLabelStyle, axisTick } from "@/components/charts/ChartShell";

interface ScoreDistributionProps {
  metrics: Metrics | null;
  tasks?: Record<string, any>;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    // Skip spacer bars
    if (data.isSpacer) return null;

    const score = payload[0].value;
    return (
      <div className="chart-tooltip">
        <p className="font-semibold text-foreground">{data.fullLabel}</p>
        <p className="text-muted-foreground">
          Score: <span className="font-semibold tabular-nums text-foreground">{score?.toFixed(1) ?? 'n.v.t.'}</span> / 5
        </p>
      </div>
    );
  }
  return null;
};

const TITLE = "Scores per vraag en run";

// Parse question_id format: q001_run1, q002_run2, etc.
interface ParsedQuestionId {
  questionNum: number;
  runNum: number;
  original: string;
}

function parseQuestionId(questionId: string): ParsedQuestionId | null {
  // Match patterns like: q001_run5, q001_finance_run5, q002_hr_run3, etc.
  const match = questionId.match(/q(\d+)(?:_\w+)?_run(\d+)/);
  if (!match) return null;
  return {
    questionNum: parseInt(match[1], 10),
    runNum: parseInt(match[2], 10),
    original: questionId
  };
}

export function ScoreDistribution({ metrics, tasks }: ScoreDistributionProps) {
  const chartData = useMemo(() => {
    if (!metrics || !tasks) {
      return [];
    }

    // Extract all completed tasks with scores
    const taskList = Object.entries(tasks)
      .filter(([_, task]: [string, any]) => (task.status === 'completed' || task.status === 'success') && task.accuracy_score !== undefined && task.accuracy_score !== null)
      .map(([questionId, task]: [string, any]) => {
        const parsed = parseQuestionId(questionId);
        return parsed ? {
          ...parsed,
          score: task.accuracy_score,
          questionId
        } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // If no completed tasks with scores, return empty
    if (taskList.length === 0) {
      return [];
    }

    // Sort by question number first, then by run number
    taskList.sort((a, b) => {
      if (a.questionNum !== b.questionNum) {
        return a.questionNum - b.questionNum;
      }
      return a.runNum - b.runNum;
    });

    // Group by question number and add spacers
    const chartData: any[] = [];
    let currentQuestion = -1;

    taskList.forEach((item) => {
      // Add spacer when moving to a new question (except for first question)
      if (item.questionNum !== currentQuestion) {
        if (currentQuestion !== -1) {
          // Add a small spacer bar between question groups
          chartData.push({
            label: '',
            score: 0,
            isSpacer: true,
            fullLabel: ''
          });
        }
        currentQuestion = item.questionNum;
      }

      // Short label: "1.2" means Q1 Run2
      chartData.push({
        label: `${item.questionNum}.${item.runNum}`,
        score: item.score,
        isSpacer: false,
        fullLabel: `Vraag ${item.questionNum}, run ${item.runNum}`,
        questionNum: item.questionNum,
        runNum: item.runNum
      });
    });

    return chartData;
  }, [metrics, tasks]);

  const getBarColor = (score: number, isSpacer: boolean) => {
    if (isSpacer) return "transparent"; // Invisible spacer
    return scoreColor(score);
  };

  if (!metrics || chartData.length === 0) {
    return <ChartEmpty title={TITLE} />;
  }

  return (
    <div className="flex h-full w-full flex-col">
      <ChartHeader title={TITLE} subtitle="Label V.R: bijv. 1.2 = vraag 1, run 2" />

      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ ...axisTick, fontSize: 9 }}
              stroke={chart.axis}
              tickLine={false}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              domain={[0, 5]}
              ticks={[0, 1, 2, 3, 4, 5]}
              tick={axisTick}
              stroke={chart.axis}
              tickLine={false}
              width={44}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: axisLabelStyle }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: chart.grid, fillOpacity: 0.6 }} />
            <Bar dataKey="score" radius={[4, 4, 0, 0]} maxBarSize={30}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.score, entry.isSpacer)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
